import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Code, ExternalLink } from "lucide-react";

interface MortgageChainFooterProps {
  onScheduleCall?: () => void;
  onViewDeck?: () => void;
  className?: string;
}

const MortgageChainFooter: React.FC<MortgageChainFooterProps> = ({
  onScheduleCall,
  onViewDeck,
  className = "",
}) => {
  const handleScheduleCallClick = () => {
    if (onScheduleCall) {
      onScheduleCall();
    }
  };

  const handleViewDeckClick = () => {
    if (onViewDeck) {
      onViewDeck();
    }
  };

  return (
    <footer className={`relative py-16 px-6 ${className}`}>
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-5 gap-12 mb-12">
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to="/"
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/50 cursor-pointer"
              >
                <span className="text-xl font-bold logoCrypsey text-white">
                  Crypsey
                </span>
              </Link>
            </div>
            <p className="text-gray-400 mb-6 max-w-md text-left">
              Building Canada&apos;s Bloomberg Terminal for Mortgage Data.
              Blockchain-verified, real-time analytics for institutional
              investors.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <Link
                to="/docs"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/50"
              >
                <Code className="w-5 h-5" />
                Developer API
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex gap-4">
              <a
                href="mailto:patrick@crypsey.com"
                className="glassmorphic p-3 rounded-lg hover:border-cyan-400 transition-all"
              >
                <Mail className="w-5 h-5" />
              </a>
              <a
                href="tel:+14379858199"
                className="glassmorphic p-3 rounded-lg hover:border-cyan-400 transition-all"
              >
                <Phone className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-bold mb-4 text-left">Platform</h4>
            <ul className="space-y-3 text-gray-400 text-left">
              <li className="text-left">
                <a
                  href="#problem"
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  Problem
                </a>
              </li>
              <li className="text-left">
                <a
                  href="#solution"
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  Features
                </a>
              </li>
              <li className="text-left">
                <a
                  href="#how-it-works"
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  How It Works
                </a>
              </li>
              <li className="text-left">
                <a
                  href="#use-cases"
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  Use Cases
                </a>
              </li>
              <li className="text-left">
                <a
                  href="#technology"
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  Technology
                </a>
              </li>
              {/* <li className="text-left">
                <a
                  href="#pricing"
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  Pricing
                </a>
              </li> */}
              <li className="text-left">
                <a
                  href="#api"
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  API Docs
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4 text-left">Developers</h4>
            <ul className="space-y-3 text-gray-400 text-left">
              <li className="text-left">
                <Link
                  to="/docs"
                  className="hover:text-cyan-400 transition-colors flex items-center gap-2 text-left"
                >
                  API Documentation
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </li>
              <li className="text-left">
                <a
                  href="https://github.com/crypsey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-center gap-2 text-left"
                >
                  GitHub
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li className="text-left">
                <a
                  href="mailto:patrick@crypsey.com"
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  Developer Support
                </a>
              </li>
              {/* <li className="text-left">
                <a
                  href="#"
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  SDK & Libraries
                </a>
              </li> */}
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4 text-left">Resources</h4>
            <ul className="space-y-3 text-gray-400 text-left">
              <li className="text-left">
                <Link
                  to="/pitch-deck"
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  Investor Deck
                </Link>
              </li>
              <li className="text-left">
                <button
                  type="button"
                  onClick={handleScheduleCallClick}
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  Schedule Call
                </button>
              </li>
              <li className="text-left">
                <a
                  href="mailto:patrick@crypsey.com"
                  className="hover:text-cyan-400 transition-colors text-left"
                >
                  Contact Us
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-gray-500 text-sm">
            © 2025 Crypsey Corp All rights reserved.
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-gray-400">
            <Link
              to="/privacy-policy"
              className="hover:text-cyan-400 transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-service"
              className="hover:text-cyan-400 transition-colors"
            >
              Terms of Service
            </Link>
            <Link
              to="/risk-disclosures"
              className="hover:text-cyan-400 transition-colors"
            >
              Risk Disclosures
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MortgageChainFooter;
